import {
  Component,
  OnInit,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  SimpleChanges,
  ElementRef,
  HostListener,
  NgZone,
  inject,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgMultiSelectDropDownModule, IDropdownSettings } from 'ng-multiselect-dropdown';
import { Language } from '../../models/language.model';

@Component({
  selector: 'app-language-dropdown',
  standalone: true,
  imports: [FormsModule, NgMultiSelectDropDownModule],
  templateUrl: './language-dropdown.component.html',
  styleUrl: './language-dropdown.component.scss',
})
export class LanguageDropdownComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private readonly el = inject(ElementRef);
  private readonly zone = inject(NgZone);
  private observer: MutationObserver | null = null;

  readonly languages = input.required<Language[]>();
  readonly value = input<string>('');
  readonly filterBy = input<string>('');
  readonly singleSelection = input<boolean>(false);
  readonly selectedLanguages = input<Language[]>([]);

  // ── Outputs ───────────────────────────────────────────────────────────
  readonly addLanguage = output<Language>();
  readonly addLanguages = output<Language[]>();
  readonly removeLanguage = output<Language>();
  readonly blurred = output<void>();

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    const related = event.relatedTarget as Node | null;
    if (!related || !this.el.nativeElement.contains(related)) {
      this.blurred.emit();
    }
  }

  filteredLanguages: Language[] = [];
  selectedItems: Language[] = [];
  langDropdownSettings: IDropdownSettings = {};

  ngOnInit(): void {
    this.filterLanguages();
    this.setLangDropdownSettings();
  }

  ngAfterViewInit(): void {
    this.observeDropdownOpen();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  /**
   * Uses a MutationObserver to watch for the `hidden` attribute being
   * removed from `.dropdown-list`, which is how ng-multiselect-dropdown
   * toggles visibility. When the list becomes visible, we focus the
   * search input so the user can start typing immediately.
   */
  private observeDropdownOpen(): void {
    const dropdownList = this.el.nativeElement.querySelector('.dropdown-list') as HTMLElement | null;
    if (!dropdownList) return;

    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => {
        if (!dropdownList.hidden) {
          const searchInput =
            dropdownList.querySelector<HTMLInputElement>('.filter-textbox input');
          searchInput?.focus();
        }
      });

      this.observer.observe(dropdownList, {
        attributes: true,
        attributeFilter: ['hidden'],
      });
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['languages'] || changes['filterBy'] || changes['value']) {
      this.filterLanguages();
      this.setLangDropdownSettings();
    }

    if (changes['selectedLanguages']) {
      this.selectedItems = [...this.selectedLanguages()];
    }
    if (changes['value'] && this.singleSelection()) {
      const code = this.value();
      if (code) {
        const match = this.languages().find((l) => l.code === code);
        this.selectedItems = match ? [match] : [];
      } else {
        this.selectedItems = [];
      }
    }
  }

  private filterLanguages(): void {
    const filter = this.filterBy();
    const all = this.languages();

    if (filter) {
      this.filteredLanguages = all.filter((lang) => lang.code !== filter);
    } else {
      this.filteredLanguages = [...all];
    }

    if (this.singleSelection() && this.filteredLanguages.length === 1) {
      this.selectedItems = [...this.filteredLanguages];
      this.addLanguage.emit(this.filteredLanguages[0]);
    }
  }

  private setLangDropdownSettings(): void {
    const multipleLanguages = this.filteredLanguages.length > 1;

    this.langDropdownSettings = {
      idField: 'code',
      textField: 'name',
      allowSearchFilter: multipleLanguages,
      itemsShowLimit: 4,
      enableCheckAll: multipleLanguages && !this.singleSelection(),
      singleSelection: this.singleSelection(),
      closeDropDownOnSelection: this.singleSelection(),
      selectAllText: 'Select All',
      unSelectAllText: 'Unselect All',
      searchPlaceholderText: 'Search',
    };
  }

  onSelectLang(item: any): void {
    this.addLanguage.emit(item as Language);
  }

  onDeSelectLang(item: any): void {
    this.removeLanguage.emit(item as Language);
  }

  onSelectAll(items: any[]): void {
    this.addLanguages.emit(items as Language[]);
  }
}
